import { TrendingUp, TrendingDown, Award, AlertTriangle, Building2 } from "lucide-react";
import { fmtCompact, fmtCurrency, fmtPct, fmtSignedCompact } from "../../lib/format";

function Chip({ icon: Icon, tone, testid, title, children }) {
  const toneCls = {
    pos: "bg-brand-pos/10 text-brand-pos",
    neg: "bg-brand-neg/10 text-brand-neg",
    gold: "bg-brand-gold/15 text-brand-gold",
    ink: "bg-brand-ink/10 text-brand-ink",
  }[tone];
  return (
    <div className="flex items-center gap-3 bg-brand-surface px-5 py-4" data-testid={testid}>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${toneCls}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">{title}</p>
        <p className="text-sm text-brand-ink truncate">{children}</p>
      </div>
    </div>
  );
}

function renderChip(ins) {
  switch (ins.kind) {
    case "mover":
      return (
        <Chip key="mover" testid="insight-mover" title={ins.up ? "Biggest gain" : "Biggest drop"}
          icon={ins.up ? TrendingUp : TrendingDown} tone={ins.up ? "pos" : "neg"}>
          <span className="font-medium">{ins.location}</span>{" "}
          {ins.up ? "up" : "down"} {fmtCompact(Math.abs(ins.value))} in net income
        </Chip>
      );
    case "top":
      return (
        <Chip key="top" testid="insight-top" title="Top earner" icon={Award} tone="gold">
          <span className="font-medium">{ins.location}</span> leads at {fmtCurrency(ins.value)} net income
        </Chip>
      );
    case "loss":
      return (
        <Chip key="loss" testid="insight-watch" title="Watch" icon={AlertTriangle} tone="neg">
          <span className="font-medium">{ins.location}</span> is at a loss of {fmtCurrency(Math.abs(ins.value))}
        </Chip>
      );
    case "occupancy":
      return (
        <Chip key="occupancy" testid="insight-watch" title="Most room to grow" icon={Building2} tone="ink">
          <span className="font-medium">{ins.location}</span> is {fmtPct(ins.value)} occupied
        </Chip>
      );
    default:
      return null;
  }
}

// Portfolio highlights: 3 significant stats across locations (All Locations view),
// or a single net-income headline when viewing one location.
export default function AutoInsight({ summary, isRange }) {
  const insights = summary?.insights || [];

  if (insights.length > 0) {
    return (
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary mb-2">Portfolio Highlights</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border border border-brand-border"
          data-testid="auto-insight">
          {insights.map(renderChip)}
        </div>
      </div>
    );
  }

  // Single-location fallback: one net-income line.
  const d = summary?.comparison?.deltas?.net_income;
  if (!d || d.pct == null) return null;
  const up = d.pct >= 0;
  const pct = Math.abs(d.pct).toFixed(1);
  const mode = summary.comparison.mode;
  const period = mode === "yoy" ? "year over year" : !isRange ? "month over month" : "vs the prior period";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center gap-3 bg-brand-surface border border-brand-border rounded-lg px-5 py-3.5"
      data-testid="auto-insight">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${up ? "bg-brand-pos/10 text-brand-pos" : "bg-brand-neg/10 text-brand-neg"}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <p className="text-sm text-brand-ink">
        Net income is{" "}
        <span className={up ? "text-brand-pos font-medium" : "text-brand-neg font-medium"}>{up ? "up" : "down"} {pct}%{d.abs != null ? ` (${fmtSignedCompact(d.abs)})` : ""}</span>{" "}
        {period}.
      </p>
    </div>
  );
}
